// BriefBioPage.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import './Bulletin.css';

const ABBR = [
  'mr.', 'mrs.', 'ms.', 'dr.', 'prof.', 'sr.', 'jr.',
  'inc.', 'ltd.', 'co.', 'vs.', 'etc.', 'e.g.', 'i.e.'
];

const isAbbreviation = (word) => {
  const cleaned = word.toLowerCase().replace(/[^a-z.]/g, '');
  return ABBR.includes(cleaned);
};

const splitIntoSentences = (text = '') => {
  const words = text.trim().split(/\s+/);
  const sentences = [];
  let currentSentence = [];

  words.forEach((word, idx) => {
    currentSentence.push(word);
    const isLastWord = idx === words.length - 1;
    const endsWithPunctuation = /[.!?]$/.test(word);

    if ((endsWithPunctuation && !isAbbreviation(word)) || isLastWord) {
      sentences.push(currentSentence.join(' '));
      currentSentence = [];
    }
  });

  return sentences;
};

const BriefBioPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [bio, setBio] = useState(null);
  const [hasClickedName, setHasClickedName] = useState(false);

  useEffect(() => {
    const fetchBio = async () => {
      try {
        const docRef = doc(db, 'briefBios', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBio({ id: docSnap.id, ...docSnap.data() });
        } else {
          console.error('Bio not found');
        }
      } catch (error) {
        console.error('Error fetching bio:', error);
      }
    };

    fetchBio();
  }, [id]);

  useEffect(() => {
    if (bio?.name) {
      const clicked = JSON.parse(localStorage.getItem('clickedNames') || '[]');
      if (!clicked.includes(bio.name)) {
        clicked.push(bio.name);
        localStorage.setItem('clickedNames', JSON.stringify(clicked));
      }
      setHasClickedName(true);
    }
  }, [bio]);

  const handleEdit = () => {
    navigate(`/briefbioform?edit=${bio.id}&type=briefBio`);
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this bio?')) {
      try {
        await deleteDoc(doc(db, 'briefBios', bio.id));
        navigate('/');
      } catch (error) {
        console.error('Error deleting bio:', error);
        alert('Failed to delete bio.');
      }
    }
  };

  if (!bio) return <p>Loading...</p>;

  const sentences = splitIntoSentences(bio.bio || '');

  return (
    <div className="face-voting-container">
      <div className="face-question-box">
      <h2
        style={{
            cursor: 'default',
            color: hasClickedName ? '#551A8B' : '#1a0dab',
            textDecoration: 'none',
        }}
        >
        {bio.name}
        </h2>

        <div className="faces-row" style={{ justifyContent: 'center' }}>
          <div className="face-card">
            <div
              className="face-image-border-wrapper"
              style={{ width: 260, height: 260 }}
            >
              <img
                src={bio.imageUrl}
                alt={bio.name}
                className="voting-face-img"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20, fontSize: 16, lineHeight: 1.6, color: '#333', textAlign: 'left' }}>
          {sentences.length > 1 ? (
            <ul style={{ listStyleType: 'disc', marginLeft: '20px', paddingInlineStart: '20px' }}>
              {sentences.map((sentence, idx) => (
                <li key={idx} style={{ textAlign: 'left' }}>{sentence}</li>
              ))}
            </ul>
          ) : (
            <p>{bio.bio}</p>
          )}
        </div>

        {bio.sources?.length > 0 && (
          <div style={{ marginTop: 15 }}>
            <h4 style={{ marginBottom: 6, fontSize: 15 }}>Sources:</h4>
            <ul style={{ paddingLeft: 18, color: '#1a0dab', fontSize: 15 }}>
              {bio.sources.map((src, idx) => (
                src.name && src.link ? (
                  <li key={idx}>
                    <a
                      href={src.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1a0dab', textDecoration: 'underline' }}
                    >
                      {src.name}
                    </a>
                  </li>
                ) : null
              ))}
            </ul>
          </div>
        )}

        {isAdmin && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
            <button
              onClick={handleEdit}
              style={{
                background: '#4CAF50',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '6px 12px',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
              }}
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              style={{
                background: '#F44336',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '6px 12px',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BriefBioPage;
